import React, { useEffect, useState } from 'react'
import {
	Box,
	CircularProgress,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from '@mui/material'
import { AppCardTitle, AppEmptyLine, AppResultsSkeleton, AppNote } from '@components'

/**
 * Categories Upgrade analytics — one row per billing SIPP, showing how
 * often the car booked got upgraded and the revenue captured.
 *
 *   <UpgradeAnalyticsTable from="2026-01-01" to="2026-06-30"
 *                          offices={['A1']} categories={[]} />
 */
const fmtNum  = (n) => new Intl.NumberFormat('es-ES').format(n)
const fmtMoney = (n) =>
	new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
		.format(n)
const fmtPct = (n) =>
	new Intl.NumberFormat('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
		.format(n) + '%'

export default function UpgradeAnalyticsTable({
	from, to, offices = [], categories = [],
	channels = [], partners = [], duration = 'total',
}) {
	const [data, setData] = useState(null)
	const [error, setError] = useState(null)
	const [loading, setLoading] = useState(true)

	useEffect(() => {
		if (!from || !to) return
		setLoading(true); setError(null)
		const qs = new URLSearchParams({ from, to })
		if (offices.length)    qs.set('offices', offices.join(','))
		if (categories.length) qs.set('sipps',   categories.join(','))
		if (channels.length)   qs.set('channel_types', channels.join(','))
		if (partners.length)   qs.set('partners',      partners.join(','))
		if (duration && duration !== 'total') qs.set('duration', duration)
		fetch(`/api/my-business/upgrade-analytics?${qs}`, { credentials: 'include' })
			.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
			.then(setData)
			.catch(e => setError(e.message))
			.finally(() => setLoading(false))
	}, [from, to, offices.join(','), categories.join(','),
	    channels.join(','), partners.join(','), duration])

	if (error) return (
		<Paper sx={{ p: 2 }}>
			<AppNote severity="error">Upgrade analytics unreachable: {error}</AppNote>
		</Paper>
	)
	// Loading (also a refetch): the card's skeleton, so nothing can be clicked half-loaded
	if (loading || !data) return <AppResultsSkeleton rows={8}/>
	const rows = data.rows || []
	if (!rows.length) return (
		<Paper sx={{ p: 3 }}>
			<AppEmptyLine>No SIPPs with enough bookings in this range.</AppEmptyLine>
		</Paper>
	)
	return (
		<Paper sx={{ p: 2.5 }}>
			<AppCardTitle icon="arrow-up-right-dots" title="Categories Upgrade — by billing SIPP"/>
			<TableContainer sx={{ mt: 1.5 }}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>SIPP</TableCell>
							<TableCell>Class</TableCell>
							<TableCell align="right">Bookings</TableCell>
							<TableCell align="right">Upgraded</TableCell>
							<TableCell align="right">Upgrade&nbsp;%</TableCell>
							<TableCell align="right">Avg revenue</TableCell>
							<TableCell align="right">Avg upgrade revenue</TableCell>
							<TableCell align="right">Upgrade revenue</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map(r => (
							<TableRow key={r.billing_sipp}>
								<TableCell><b>{r.billing_sipp}</b></TableCell>
								<TableCell>{r.vehicle_class || '—'}</TableCell>
								<TableCell align="right">{fmtNum(r.bookings)}</TableCell>
								<TableCell align="right">{fmtNum(r.upgraded)}</TableCell>
								<TableCell align="right">{fmtPct(r.upgrade_pct)}</TableCell>
								<TableCell align="right">{fmtMoney(r.avg_revenue)}</TableCell>
								<TableCell align="right">
									{r.avg_upgrade_revenue ? fmtMoney(r.avg_upgrade_revenue) : '—'}
								</TableCell>
								<TableCell align="right">{fmtMoney(r.upgrade_revenue)}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		</Paper>
	)
}
